import React, { useEffect, useMemo, useRef } from 'react';
import { FilterMatchMode } from 'primereact/api';
import moment from 'moment-timezone';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dropdown } from 'react-bootstrap';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Button } from 'primereact/button';
import { useHistory } from 'react-router';
import { user } from '../../../redux/user';
import { managementCompany, setManagementCompany } from '../../../redux/managementCompany';
import {
	viewStateForWorkforceProfessionals,
	updateProfessionalsViewStateForManagementCompany,
	updateLocationsViewStateForManagementCompany,
	viewStateForWorkforceLocations,
	updateShiftMedViewStateForManagementCompany,
	viewStateForWorkforceShiftMed,
	updateActiveTab,
	activeTab
} from '../../../redux/flexWorkforceView';
import WithOnlyOnDesktop from '../../../components/onlyOnDesktop/withOnlyOnDesktop';
import FlexProfessionalsList from './FlexProfessionalsList';
import FlexLocationsList from './FlexLocations';
import ShiftMedOnDemandStaffList from './ShiftMedOnDemandStaffList';
import MANAGEMENT_COMPANIES_PERMISSIONS from '../../../constants/ManagementCompaniesPermissions';
import arrow from '../../../assets/images/keyboard_arrow_down.svg';
import { ReactComponent as DownloadIcon } from '../../../assets/images/download.svg';
import utils from '../../../utils/utils';
import IsAuthorizedForManagementCompany from '../../../components/IsAuthorizedForManagementCompany/IsAuthorizedForManagementCompany';
import { PERMISSION, PRIVATE_PATH } from '../../../constants';
import TextSearchComponent from '../../../components/SearchComponent/TextSearchComponent';

import './FlexWorkforceScreen.scss';

const DEFAULT_FILTERS = { global: { value: '', matchMode: FilterMatchMode.CONTAINS } };

// Workforce tabs in display order; a tab renders only when the user holds its permission on the selected MC.
const WORKFORCE_TABS = [
	{ key: 'professionals', permission: PERMISSION.WORKFORCE_VIEW },
	{ key: 'locations', permission: PERMISSION.WORKFORCE_LOCATIONS_VIEW },
	{ key: 'shiftmed', permission: PERMISSION.WORKFORCE_SHIFTMED_VIEW }
];

const FlexWorkforceScreen = () => {
	const history = useHistory();
	const dispatch = useDispatch();
	const workersDataTableRef = useRef(null);
	const locationsDataTableRef = useRef(null);

	const userData = useSelector(user, shallowEqual);
	const managementCompanyData = useSelector(managementCompany, shallowEqual);

	const storedTabKey = useSelector(activeTab);
	const currentManagementCompanyId = managementCompanyData?.id;
	const visibleTabs = useMemo(
		() =>
			WORKFORCE_TABS.filter((tab) =>
				utils.isAuthorizedExact(tab.permission, userData.managementCompanyPermissions[currentManagementCompanyId])
			),
		[userData, currentManagementCompanyId]
	);
	const visibleTabKeys = visibleTabs.map((tab) => tab.key);
	// Fall back to the first visible tab when the stored key is not available for the selected MC
	// (permission sets differ between MCs, or a stale persisted value).
	const activeTabKey = visibleTabKeys.includes(storedTabKey) ? storedTabKey : visibleTabKeys[0];
	// Each TabPanel keeps its fixed WORKFORCE_TABS slot in the JSX; a hidden tab renders as `false`, which
	// React.Children.map still counts as a child index. So TabView.activeIndex must be the real slot of the
	// active tab, not its compacted position in visibleTabKeys.
	const activeTabIndex = Math.max(
		0,
		WORKFORCE_TABS.findIndex((tab) => tab.key === activeTabKey)
	);

	const isProfessionalsActive = activeTabKey === 'professionals';
	const isLocationsActive = activeTabKey === 'locations';
	const isShiftMedActive = activeTabKey === 'shiftmed';
	const showProfessionalsTab = visibleTabKeys.includes('professionals');
	const showLocationsTab = visibleTabKeys.includes('locations');
	const showShiftMedTab = visibleTabKeys.includes('shiftmed');
	const professionalsViewState = useSelector(viewStateForWorkforceProfessionals(currentManagementCompanyId));
	const professionalFilters = professionalsViewState?.filters || DEFAULT_FILTERS;
	const professionalSearchTerm = professionalFilters.global?.value || '';
	const locationsViewState = useSelector(viewStateForWorkforceLocations(currentManagementCompanyId));
	const locationFilters = locationsViewState?.filters || DEFAULT_FILTERS;
	const locationSearchTerm = locationFilters.global?.value || '';
	const shiftMedViewState = useSelector(viewStateForWorkforceShiftMed(currentManagementCompanyId));
	const shiftMedFilters = shiftMedViewState?.filters || DEFAULT_FILTERS;
	const shiftMedSearchTerm = shiftMedFilters.global?.value || '';

	const setProfessionalFilters = (updater) => {
		if (!currentManagementCompanyId) return;
		const next = typeof updater === 'function' ? updater(professionalFilters) : updater;
		dispatch(
			updateProfessionalsViewStateForManagementCompany({
				managementCompanyId: currentManagementCompanyId,
				filters: next
			})
		);
	};

	const setLocationFilters = (updater) => {
		if (!currentManagementCompanyId) return;
		const next = typeof updater === 'function' ? updater(locationFilters) : updater;
		dispatch(
			updateLocationsViewStateForManagementCompany({ managementCompanyId: currentManagementCompanyId, filters: next })
		);
	};

	const setShiftMedFilters = (updater) => {
		if (!currentManagementCompanyId) return;
		const next = typeof updater === 'function' ? updater(shiftMedFilters) : updater;
		dispatch(
			updateShiftMedViewStateForManagementCompany({ managementCompanyId: currentManagementCompanyId, filters: next })
		);
	};

	const managementCompaniesOptions = useMemo(() => {
		const uniqueManagementCompanyIds = new Set();
		const options = [];

		userData?.facilitiesEnabled?.forEach((facility) => {
			const mcId = facility.management_company_id;
			const managementCompanyPermissions = userData.managementCompanyPermissions[mcId];
			const hasWorkforcePagePermission =
				utils.isAuthorizedExact(MANAGEMENT_COMPANIES_PERMISSIONS.WORKFORCE_ACTION_VIEW, managementCompanyPermissions) ||
				utils.isAuthorizedExact(PERMISSION.WORKFORCE_SHIFTMED_VIEW, managementCompanyPermissions);
			if (!uniqueManagementCompanyIds.has(mcId) && hasWorkforcePagePermission && facility.allow_workforce_page) {
				uniqueManagementCompanyIds.add(mcId);
				options.push({
					id: mcId,
					name: facility.management_company_name
				});
			}
		});

		return options.sort((a, b) => a.name.localeCompare(b.name));
	}, [userData]);

	const handleManagementCompanyChange = (newManagementCompany) => {
		dispatch(setManagementCompany(newManagementCompany));
	};

	useEffect(() => {
		if (!managementCompaniesOptions.length) {
			history.push(PRIVATE_PATH.SHIFTS);
			return;
		}

		if (!managementCompanyData?.id && managementCompaniesOptions.length) {
			handleManagementCompanyChange(managementCompaniesOptions[0]);
		}
	}, [managementCompaniesOptions]);

	const ManagementCompanyDropdown = () => {
		if (managementCompaniesOptions?.length < 2) {
			return <div className="title">Workforce - {managementCompanyData?.name}</div>;
		}
		return (
			<div className="dropdown-management-company">
				<Dropdown>
					<Dropdown.Toggle>
						<div className="title">Workforce - {managementCompanyData?.name}</div>
						<img src={arrow} alt="expand" className="keyboard-arrow-down-icon" />
					</Dropdown.Toggle>
					<Dropdown.Menu>
						{managementCompaniesOptions.map((option) => (
							<Dropdown.Item
								className="dropdown-management-company__item"
								onClick={() => handleManagementCompanyChange(option)}
								key={`management-company-${option.id}`}
							>
								{option.name}
							</Dropdown.Item>
						))}
					</Dropdown.Menu>
				</Dropdown>
			</div>
		);
	};

	const exportCSV = () => {
		if (isProfessionalsActive) {
			workersDataTableRef.current.exportCSV();
		} else {
			locationsDataTableRef.current.exportCSV();
		}
	};
	const onProfessionalSearch = (value) => {
		setProfessionalFilters((prev) => ({
			...prev,
			global: { ...(prev.global || { matchMode: FilterMatchMode.CONTAINS }), value }
		}));
	};

	const onLocationSearch = (value) => {
		setLocationFilters((prev) => ({
			...prev,
			global: { ...(prev.global || { matchMode: FilterMatchMode.CONTAINS }), value }
		}));
	};

	const onShiftMedSearch = (value) => {
		setShiftMedFilters((prev) => ({
			...prev,
			global: { ...(prev.global || { matchMode: FilterMatchMode.CONTAINS }), value }
		}));
	};

	const exportFilename = `${isLocationsActive ? 'Locations' : 'Workforce'}_${
		managementCompanyData.name
	}_${moment().format('YYYY-MM-DD_hh-mm')}`;

	const downloadPermission = isLocationsActive
		? PERMISSION.WORKFORCE_DOWNLOAD_LOCATIONS
		: PERMISSION.WORKFORCE_DOWNLOAD;

	const handleTabChange = (event) => {
		// event.index is a real WORKFORCE_TABS slot (PrimeReact only builds headers for rendered tabs, but
		// keeps their original child index), so map it back through the full descriptor array, not visibleTabKeys.
		const newTabKey = WORKFORCE_TABS[event.index].key;
		dispatch(updateActiveTab(newTabKey));
		if (newTabKey !== 'professionals') {
			onProfessionalSearch('');
		}
		if (newTabKey !== 'locations') {
			onLocationSearch('');
		}
		if (newTabKey !== 'shiftmed') {
			onShiftMedSearch('');
		}
	};

	return (
		<WithOnlyOnDesktop>
			<div className="flex-workforce-screen">
				<div className="title-wrapper">
					<ManagementCompanyDropdown />
					{isProfessionalsActive && (
						<TextSearchComponent
							placeholder="Search for..."
							className="search-container"
							searchValue={professionalSearchTerm}
							setSearchValue={onProfessionalSearch}
						/>
					)}
					{isLocationsActive && (
						<TextSearchComponent
							placeholder="Search for..."
							className="search-container"
							searchValue={locationSearchTerm}
							setSearchValue={onLocationSearch}
						/>
					)}
					{isShiftMedActive && (
						<TextSearchComponent
							placeholder="Search for..."
							className="search-container"
							searchValue={shiftMedSearchTerm}
							setSearchValue={onShiftMedSearch}
						/>
					)}
				</div>
				<div className="btn-download-container">
					{!isShiftMedActive && (
						<IsAuthorizedForManagementCompany
							exactPermission={downloadPermission}
							managementCompanyId={managementCompanyData.id}
						>
							<Button
								type="button"
								className="btn-download-list"
								label="Download report"
								outlined
								onClick={exportCSV}
								data-pr-tooltip="Download"
								icon={() => <DownloadIcon />}
							/>
						</IsAuthorizedForManagementCompany>
					)}
				</div>

				<TabView className="workforce-tab" activeIndex={activeTabIndex} onTabChange={handleTabChange}>
					{showProfessionalsTab && (
						<TabPanel header="Internal">
							{managementCompanyData?.id && (
								<FlexProfessionalsList
									managementCompany={managementCompanyData}
									dataTableRef={workersDataTableRef}
									exportFilename={exportFilename}
									filters={professionalFilters}
									setFilters={setProfessionalFilters}
								/>
							)}
						</TabPanel>
					)}
					{showLocationsTab && (
						<TabPanel header="Locations">
							{managementCompanyData?.id && (
								<FlexLocationsList
									managementCompany={managementCompanyData}
									dataTableRef={locationsDataTableRef}
									exportFilename={exportFilename}
									filters={locationFilters}
								/>
							)}
						</TabPanel>
					)}
					{showShiftMedTab && (
						<TabPanel header="ShiftMed">
							{managementCompanyData?.id && (
								<ShiftMedOnDemandStaffList managementCompany={managementCompanyData} filters={shiftMedFilters} />
							)}
						</TabPanel>
					)}
				</TabView>
			</div>
		</WithOnlyOnDesktop>
	);
};

export default FlexWorkforceScreen;
